import { useState, useRef, useCallback, useEffect } from 'react';
import { router } from '@inertiajs/react';
import { route } from '../utils/route';
import { trackSearch } from '../utils/tracking';
import type { ProductListing, SearchData, RelatedContext, UnavailableProduct } from '../types/products';

interface UseProductSearchProps {
  initialSearch?: string;
  initialSearchData?: SearchData | null;
}

export function useProductSearch({ initialSearch, initialSearchData }: UseProductSearchProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearch || '');
  const [semanticEnabled, setSemanticEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('semantic') === '1';
  });
  // committedTerm is the term that was actually submitted
  const [committedTerm, setCommittedTerm] = useState(initialSearch || '');
  const [searchResults, setSearchResults] = useState<ProductListing[]>(initialSearchData?.products || []);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [searchHasMore, setSearchHasMore] = useState(initialSearchData?.hasMore || false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotal, setSearchTotal] = useState(initialSearchData?.total || 0);
  const [searchRelated, setSearchRelated] = useState<ProductListing[]>(initialSearchData?.relatedProducts || []);
  const [searchRelatedContext, setSearchRelatedContext] = useState<RelatedContext | null>(initialSearchData?.relatedContext ?? null);
  const [searchUnavailable, setSearchUnavailable] = useState<UnavailableProduct[]>(initialSearchData?.unavailableMatches ?? []);
  const abortRef = useRef<AbortController | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Track whether initial data has been consumed (skip first fetch if SSR provided data)
  // Don't use SSR data when semantic mode is active (SSR is always full-text)
  const initialDataUsed = useRef(!!initialSearchData && !semanticEnabled);
  // Once the user manually picks a mode for the current term, don't let the auto-fallback override it
  const userPickedModeRef = useRef(false);

  // Track SSR search results on mount
  useEffect(() => {
    if (initialSearch && initialSearchData && initialSearchData.total > 0) {
      trackSearch(initialSearch, initialSearchData.total);
    }
  }, []);

  // Submit search — called on Enter key
  const submitSearch = useCallback((term?: string) => {
    const t = (term ?? searchTerm).trim();
    if (t !== committedTerm) {
      userPickedModeRef.current = false;
    }
    setCommittedTerm(t);
    if (!t || t.length < 2) {
      setSearchResults([]);
      setSearchHasMore(false);
      setSearchPage(1);
      setSearchTotal(0);
      setSearchRelated([]);
      setSearchRelatedContext(null);
      setSearchUnavailable([]);
    }
  }, [searchTerm, committedTerm]);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setCommittedTerm('');
    setSearchResults([]);
    setSearchHasMore(false);
    setSearchPage(1);
    setSearchTotal(0);
    setSearchRelated([]);
    setSearchRelatedContext(null);
    setSearchUnavailable([]);
    setSemanticEnabled(false);
    userPickedModeRef.current = false;
  }, []);

  // Wrapper exposed to the toggle: marks the choice as user-driven so the auto-fallback won't override it
  const userToggleSemantic = useCallback((enabled: boolean) => {
    userPickedModeRef.current = true;
    setSemanticEnabled(enabled);
  }, []);

  // Fetch search results (page 1) when committedTerm changes
  useEffect(() => {
    const term = committedTerm.trim();
    if (!term || term.length < 2) {
      return;
    }

    // Skip fetch if we already have SSR data for this exact term
    if (initialDataUsed.current) {
      initialDataUsed.current = false;
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoadingSearch(true);
    setSearchPage(1);

    const url = semanticEnabled
      ? `${route('api.products.semantic-search')}?q=${encodeURIComponent(term)}&page=1`
      : `${route('api.products.index')}?search=${encodeURIComponent(term)}&page=1`;

    fetch(url, {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(r => r.json())
      .then(data => {
        // La búsqueda por ideas (semántica) nunca se activa sola: solo cuando
        // el cliente prende el toggle (#100). El auto-fallback con < 2
        // resultados quedó eliminado a propósito.
        setSearchResults(data.products);
        setSearchHasMore(data.hasMore);
        setSearchTotal(data.total);
        setSearchRelated(data.relatedProducts ?? []);
        setSearchRelatedContext(data.relatedContext ?? null);
        setSearchUnavailable(data.unavailableMatches ?? []);
        setSearchPage(1);
        setIsLoadingSearch(false);
        trackSearch(term, data.total);
      })
      .catch(err => {
        if (err.name !== 'AbortError') setIsLoadingSearch(false);
      });

    return () => controller.abort();
  }, [committedTerm, semanticEnabled]);

  // Load next page of search results
  const loadMoreSearch = useCallback(() => {
    const term = committedTerm.trim();
    if (!term || isLoadingSearch || !searchHasMore) return;

    setIsLoadingSearch(true);
    const nextPage = searchPage + 1;

    const url = semanticEnabled
      ? `${route('api.products.semantic-search')}?q=${encodeURIComponent(term)}&page=${nextPage}`
      : `${route('api.products.index')}?search=${encodeURIComponent(term)}&page=${nextPage}`;

    fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(r => r.json())
      .then(data => {
        setSearchResults(prev => [...prev, ...data.products]);
        setSearchHasMore(data.hasMore);
        setSearchPage(nextPage);
        setIsLoadingSearch(false);
      })
      .catch(() => {
        setIsLoadingSearch(false);
      });
  }, [committedTerm, isLoadingSearch, searchHasMore, searchPage, semanticEnabled]);

  // IntersectionObserver for search results pagination
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) loadMoreSearch();
      },
      { rootMargin: '400px' },
    );

    const sentinel = sentinelRef.current;
    if (sentinel && searchHasMore) {
      observerRef.current.observe(sentinel);
    }

    return () => observerRef.current?.disconnect();
  }, [loadMoreSearch, searchHasMore]);

  // Sync committed search term + semantic mode to URL params (shareable URLs without reload)
  useEffect(() => {
    const url = new URL(window.location.href);
    const term = committedTerm.trim();
    if (term.length >= 2) {
      url.searchParams.set('p', term);
    } else {
      url.searchParams.delete('p');
    }
    if (semanticEnabled) {
      url.searchParams.set('semantic', '1');
    } else {
      url.searchParams.delete('semantic');
    }
    if (url.href !== window.location.href) {
      router.visit(url.pathname + url.search, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        only: [],
      });
    }
  }, [committedTerm, semanticEnabled]);

  const isSearching = committedTerm.trim().length >= 2;

  return {
    searchTerm,
    setSearchTerm,
    submitSearch,
    clearSearch,
    searchResults,
    isSearching,
    isLoadingSearch,
    searchHasMore,
    searchTotal,
    searchRelated,
    searchRelatedContext,
    searchUnavailable,
    searchSentinelRef: sentinelRef,
    semanticEnabled,
    setSemanticEnabled: userToggleSemantic,
  };
}
